import React from 'react';
import { motion } from 'motion/react';
import { useEffect, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';

const buildKeyframes = (from, steps) => {
  const keys = new Set([...Object.keys(from), ...steps.flatMap(s => Object.keys(s))]);

  const keyframes = {};
  keys.forEach(k => {
    keyframes[k] = [from[k], ...steps.map(s => s[k])];
  });
  return keyframes;
};

const BlurText = ({
  text = '',
  delay = 200,
  className = '',
  animateBy = 'words',
  direction = 'top',
  threshold = 0.1,
  rootMargin = '0px',
  animationFrom,
  animationTo,
  easing = t => t,
  onAnimationComplete,
  stepDuration = 0.28,
  as = 'p',
  children,
}) => {
  const elements = useMemo(() => {
    if (children != null) return ['__children__'];
    if (animateBy === 'words') return text.split(' ');
    if (animateBy === 'letters' || animateBy === 'characters' || animateBy === 'chars') return text.split('');
    // 'block' or any other value → animate whole text as one unit
    return [text];
  }, [text, animateBy, children]);
  const [inView, setInView] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.unobserve(ref.current);
        }
      },
      { threshold, rootMargin }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threshold, rootMargin]);

  const defaultFrom = useMemo(
    () =>
      direction === 'top' ? { filter: 'blur(6px)', opacity: 0, y: -20 } : { filter: 'blur(6px)', opacity: 0, y: 20 },
    [direction]
  );

  const defaultTo = useMemo(
    () => [
      {
        filter: 'blur(2px)',
        opacity: 0.5,
        y: direction === 'top' ? 3 : -3
      },
      { filter: 'blur(0px)', opacity: 1, y: 0 }
    ],
    [direction]
  );

  const fromSnapshot = animationFrom ?? defaultFrom;
  const toSnapshots = animationTo ?? defaultTo;

  const stepCount = toSnapshots.length + 1;
  const totalDuration = stepDuration * (stepCount - 1);
  const times = Array.from({ length: stepCount }, (_, i) => (stepCount === 1 ? 0 : i / (stepCount - 1)));

  const Tag = as;
  const containerStyle = useMemo(() => ({
    display: as === 'span' ? 'inline-flex' : 'flex',
    flexWrap: 'wrap',
  }), [as]);
  return (
    <Tag ref={ref} className={className} style={containerStyle}>
      {elements.map((segment, index) => {
        const animateKeyframes = buildKeyframes(fromSnapshot, toSnapshots);

        const spanTransition = {
          duration: totalDuration,
          times,
          // If animating by block, do not stagger; otherwise stagger by index
          delay: (elements.length === 1 ? 0 : (index * delay) / 1000)
        };
        spanTransition.ease = easing;

        return (
          <motion.span
            className="inline-block will-change-[transform,filter,opacity]"
            key={index}
            initial={fromSnapshot}
            animate={inView ? animateKeyframes : fromSnapshot}
            transition={spanTransition}
            onAnimationComplete={index === elements.length - 1 ? onAnimationComplete : undefined}
          >
            {children != null
              ? children
              : (
                <>
                  {segment === ' ' ? '\u00A0' : segment}
                  {animateBy === 'words' && index < elements.length - 1 && '\u00A0'}
                </>
              )}
          </motion.span>
        );
      })}
    </Tag>
  );
};

BlurText.propTypes = {
  text: PropTypes.string,
  delay: PropTypes.number,
  className: PropTypes.string,
  animateBy: PropTypes.oneOf(['block', 'words', 'letters', 'characters', 'chars']),
  direction: PropTypes.string,
  threshold: PropTypes.number,
  rootMargin: PropTypes.string,
  animationFrom: PropTypes.object,
  animationTo: PropTypes.arrayOf(PropTypes.object),
  easing: PropTypes.func,
  onAnimationComplete: PropTypes.func,
  stepDuration: PropTypes.number,
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]),
  children: PropTypes.node,
};

export default BlurText;
